/**
 * The PDApps window's card collection — the pure parts: a card's colours, how the
 * Collection sorts, and the "new to you" memory. No React, so all of it is unit-tested.
 *
 * Everything here works on what the window already loaded; nothing is asked of the
 * server, and the browser storage it keeps is a per-viewer convenience only — the page
 * works the same when storage is blocked (just without "New").
 */
import { parseMiniappIcon, defaultIconSpecFor, autoTextColor } from '@papan/types'

// ── Card colours ──────────────────────────────────────────────────────────

/** `ink`: text that reads on the two colours (the card's Run button). */
export interface CardColors { from: string; to: string; ink: string }

/** Only plain `#rrggbb` ever reaches the page's CSS — anything else falls back. */
const HEX = /^#[0-9a-fA-F]{6}$/

/**
 * A card's two colours: its icon's own background (solid or gradient), else the
 * default icon's — an image background has no colour to read. The icon spec is already
 * validated when parsed; the hex check here is a second guard, since the values are
 * written into CSS.
 */
export function cardColors(icon: string | null | undefined, name: string): CardColors {
  const spec = parseMiniappIcon(icon ?? null)
  const fallback = defaultIconSpecFor(name).bg
  const bg = spec && spec.bg.type !== 'image' ? spec.bg : fallback
  if (bg.type === 'solid' && HEX.test(bg.color)) return { from: bg.color, to: bg.color, ink: autoTextColor(bg) }
  if (bg.type === 'gradient' && HEX.test(bg.from) && HEX.test(bg.to)) return { from: bg.from, to: bg.to, ink: autoTextColor(bg) }
  return colorsOf(fallback)
}
function colorsOf(bg: ReturnType<typeof defaultIconSpecFor>['bg']): CardColors {
  if (bg.type === 'solid') return { from: bg.color, to: bg.color, ink: autoTextColor(bg) }
  if (bg.type === 'gradient') return { from: bg.from, to: bg.to, ink: autoTextColor(bg) }
  return { from: '#475569', to: '#1e293b', ink: '#ffffff' }
}

// ── Sorting ───────────────────────────────────────────────────────────────

/** In the order the menu and the sheet list them; the first is the default. */
export const COLLECTION_SORTS = ['name', 'recent', 'newest'] as const
export type CollectionSort = typeof COLLECTION_SORTS[number]

export interface Sortable {
  name: string
  /** When the person last opened it (from their history), if ever. */
  openedAt?: string | null
  /** When the app was made — only an app of the active org's drive carries it. */
  createdAt?: string | null
}

const byNameOf = (a: Sortable, b: Sortable) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
/** Newest first; items without the date go after every dated one, A→Z among themselves. */
const byDateDesc = (key: 'openedAt' | 'createdAt') => (a: Sortable, b: Sortable) => {
  const x = a[key] ?? ''
  const y = b[key] ?? ''
  if (x && y && x !== y) return x < y ? 1 : -1
  if (x && !y) return -1
  if (!x && y) return 1
  return byNameOf(a, b)
}

/** The Collection in the chosen order (a new array). */
export function sortCards<T extends Sortable>(items: readonly T[], sort: CollectionSort): T[] {
  const cmp = sort === 'name' ? byNameOf : sort === 'newest' ? byDateDesc('createdAt') : byDateDesc('openedAt')
  return [...items].sort(cmp)
}

// ── Card size ─────────────────────────────────────────────────────────────

/** Every card's width in px (2:3, a game-library capsule). One size everywhere. */
export const CARD_W = 160

// ── "New to you" ──────────────────────────────────────────────────────────

/** How many seen apps a browser remembers per org; the oldest are forgotten first. */
export const SEEN_CAP = 1000

/**
 * FNV-1a → base36. What the browser stores for an app it has shown, so its storage
 * holds no app names or ids — only these short scrambled keys.
 */
export function seenHash(key: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return (h >>> 0).toString(36)
}

/**
 * The cards to mark "New": shown now, never seen before in this browser. `seen` null =
 * the person's first visit here — nothing is new then (they'd land on a page of ribbons).
 */
export function newCards(current: readonly string[], seen: ReadonlySet<string> | null): Set<string> {
  if (!seen) return new Set()
  return new Set(current.filter(k => !seen.has(seenHash(k))))
}

/**
 * `seen` plus `keys` (hashed), capped at SEEN_CAP. What is listed now goes last, so the
 * cap forgets keys no longer listed (deleted apps, old links) first — a long-standing app
 * is never forgotten and shown "New" again while it is still there.
 */
export function rememberSeen(seen: readonly string[] | null, keys: readonly string[]): string[] {
  const now = [...new Set(keys.map(seenHash))]
  const listed = new Set(now)
  const out = [...new Set((seen ?? []).filter(h => !listed.has(h))), ...now]
  return out.length > SEEN_CAP ? out.slice(out.length - SEEN_CAP) : out
}

// ── Browser storage (per viewer; every access may throw) ──────────────────

const seenKey = (tenantId: string) => `papan.pdapps.seen.${tenantId}`

export function readSeen(tenantId: string): string[] | null {
  try {
    const raw = localStorage.getItem(seenKey(tenantId))
    if (!raw) return null
    const v = JSON.parse(raw) as unknown
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : null
  } catch { return null }
}
export function writeSeen(tenantId: string, hashes: readonly string[]): void {
  try { localStorage.setItem(seenKey(tenantId), JSON.stringify(hashes)) } catch { /* convenience only */ }
}

export function readPref<T extends string>(key: string, allowed: readonly T[]): T | null {
  try { const v = localStorage.getItem(key); return v && (allowed as readonly string[]).includes(v) ? v as T : null } catch { return null }
}
export function writePref(key: string, value: string): void {
  try { localStorage.setItem(key, value) } catch { /* convenience only */ }
}
